<template>
  <div v-if="data" class="ab">
    <div class="ab-top">
      <div class="ab-top__t">
        {{ $t('abTitle') }}
        <span class="ab-live"><span class="ab-live__dot" />{{ $t('abLive') }}</span>
      </div>
      <div class="ab-params">
        {{ $t('abParams') }}:
        <b>{{ data.settings.baseline_hours }}h</b> {{ $t('abBaseline') }} ·
        <b>${{ data.settings.cost_per_hour }}</b>/h ·
        <b>{{ (data.settings.credit_rate * 100).toFixed(1) }}%</b> {{ $t('abCredit') }}
      </div>
    </div>

    <div
      v-for="lot in data.items"
      :key="lot.quote_item_id"
      class="ab-lot"
    >
      <div class="ab-lot__head">
        <div class="ab-lot__name">{{ lot.product_name }}</div>
        <div class="ab-lot__meta">{{ lot.quantity }} · {{ lot.candidates.length }} {{ $t('abSuppliers') }}</div>
        <div class="ab-lot__why">
          <feather-icon icon="AwardIcon" size="13" class="mr-25" />
          {{ winnerReason(lot) }}
        </div>
      </div>

      <div
        v-for="c in lot.candidates"
        :key="c.supplier_branch_id"
        class="ab-row"
        :class="{ 'ab-row--leader': c.is_leader }"
      >
        <div class="ab-rank" :class="rankClass(c.rank)">{{ c.rank }}</div>
        <div class="ab-sup">
          <div class="ab-sup__n">
            {{ c.supplier_name }}
            <b-badge v-if="c.is_leader" variant="light-success" class="ml-50">{{ $t('abLeader') }}</b-badge>
            <b-badge v-if="!c.has_bid" variant="light-secondary" class="ml-50">{{ $t('abNoBid') }}</b-badge>
          </div>
          <div class="ab-why">
            <span class="ab-why__part">{{ $t('abPrice') }} <b>${{ c.price_total | money }}</b></span>
            <span class="ab-why__part" :class="{ 'ab-why__part--bad': c.freight_share > 0 }">
              {{ $t('abFreight') }}
              <b v-if="c.freight_share > 0">+${{ c.freight_share | money }}</b>
              <b v-else class="text-success">$0</b>
            </span>
            <span class="ab-why__part" :class="{ 'ab-why__part--bad': c.delivery_penalty > 0 }">
              {{ $t('abDelivery') }} {{ c.delivery_hours }}h
              <b v-if="c.delivery_penalty > 0">+${{ c.delivery_penalty | money }}</b>
              <b v-else class="text-success">✓</b>
            </span>
            <span class="ab-why__part" :class="{ 'ab-why__part--good': c.has_credit }">
              {{ $t('abCreditWord') }}
              <b v-if="c.has_credit" class="text-success">−${{ c.credit_value | money }}</b>
              <b v-else class="text-muted">✗</b>
            </span>
          </div>
        </div>
        <div class="ab-eq">
          <div class="ab-eq__k">{{ $t('abEquiv') }}</div>
          <div class="ab-eq__v" :class="{ 'ab-eq__v--leader': c.is_leader }">${{ c.equivalent | money }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    quoteId: {
      type: String,
      default: '',
    },
    pollMs: {
      type: Number,
      default: 4000,
    },
  },

  data() {
    return {
      data: null,
      timer: null,
    }
  },

  created() {
    if (this.quoteId) {
      this.load()
      this.timer = setInterval(this.load, this.pollMs)
    }
  },

  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  },

  methods: {
    load() {
      this.$store.dispatch('quotes/fetchAuctionScore', this.quoteId)
        .then(res => { this.data = res })
        .catch(() => {})
    },
    rankClass(rank) {
      if (rank === 1) return 'ab-rank--1'
      if (rank === 2) return 'ab-rank--2'
      if (rank === 3) return 'ab-rank--3'
      return 'ab-rank--n'
    },
    winnerReason(lot) {
      const name = lot.winner_name || ''
      if (lot.winner_committed && lot.winner_covers > 1) {
        return this.$t('abWhyCommitted', { name, covers: lot.winner_covers })
      }
      if (lot.winner_reason === 'consolidation') {
        return this.$t('abWhyConsolidation', { name, covers: lot.winner_covers })
      }
      return this.$t('abWhyCheapest', { name })
    },
  },
}
</script>

<style lang="scss" scoped>
.ab {
  --ab-line: #ebe9f1;
  --ab-ink: #25233a;
  --ab-muted: #6e6b7b;
  --ab-faint: #a8a5b8;
}

.ab-top {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  margin-bottom: 14px;
}
.ab-top__t { font-size: 19px; font-weight: 800; color: var(--ab-ink); display: flex; align-items: center; gap: 10px; }
.ab-live {
  display: inline-flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 800; letter-spacing: .08em;
  color: #ea5455; background: #fdeef1; padding: 4px 10px; border-radius: 99px;
}
.ab-live__dot { width: 7px; height: 7px; border-radius: 99px; background: #f43f5e; animation: ab-pulse 1.4s infinite; }
@keyframes ab-pulse { 0% { box-shadow: 0 0 0 0 rgba(244,63,94,.5); } 70% { box-shadow: 0 0 0 7px rgba(244,63,94,0); } 100% { box-shadow: 0 0 0 0 rgba(244,63,94,0); } }
.ab-params { font-size: 12px; color: var(--ab-muted); b { color: var(--ab-ink); } }

.ab-lot {
  background: #fff; border: 1px solid var(--ab-line); border-radius: 14px;
  box-shadow: 0 1px 2px rgba(34,41,47,.05), 0 4px 14px rgba(34,41,47,.05);
  margin-bottom: 12px; overflow: hidden;
}
.ab-lot__head { display: flex; align-items: baseline; gap: 10px; padding: 12px 16px; border-bottom: 1px solid var(--ab-line); flex-wrap: wrap; }
.ab-lot__name { font-size: 14px; font-weight: 800; color: var(--ab-ink); }
.ab-lot__meta { font-size: 12px; color: var(--ab-faint); }
.ab-lot__why { margin-left: auto; font-size: 11.5px; font-weight: 600; color: #1f7a4d; background: #e7f7f0; padding: 3px 9px; border-radius: 99px; display: inline-flex; align-items: center; }

.ab-row {
  display: flex; align-items: center; gap: 14px; padding: 12px 16px; border-bottom: 1px solid #f5f5f9;
  &:last-child { border-bottom: 0; }
  &--leader { background: linear-gradient(90deg, #eafaf1, rgba(234,250,241,.3)); box-shadow: inset 3px 0 0 #28c76f; }
}
.ab-rank {
  width: 26px; height: 26px; border-radius: 99px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800;
  &--1 { background: linear-gradient(135deg, #fde68a, #f59e0b); color: #7c2d12; }
  &--2 { background: #e2e8f0; color: #475569; }
  &--3 { background: #f5d3b0; color: #9a3412; }
  &--n { background: #f1f5f9; color: var(--ab-faint); }
}
.ab-sup { flex: 1; min-width: 0; }
.ab-sup__n { font-size: 13.5px; font-weight: 700; color: var(--ab-ink); }
.ab-why { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 4px; font-size: 12px; color: var(--ab-muted); font-variant-numeric: tabular-nums; }
.ab-why__part b { color: var(--ab-ink); }
.ab-why__part--bad b { color: #ea5455; }
.ab-why__part--good b { color: #28c76f; }

.ab-eq { text-align: right; flex-shrink: 0; }
.ab-eq__k { font-size: 10px; font-weight: 700; color: var(--ab-faint); text-transform: uppercase; letter-spacing: .05em; }
.ab-eq__v { font-size: 18px; font-weight: 800; color: var(--ab-ink); font-variant-numeric: tabular-nums; &--leader { color: #28c76f; } }

@media (max-width: 767px) {
  .ab-row { flex-wrap: wrap; }
  .ab-eq { text-align: left; }
}
</style>
